import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, type IndexStatusResponse } from '@/api/client';
import { DemoLayout } from '@/components/DemoLayout';
import { clearEnrollment } from '@/lib/enrollment';
import type { SuccessState } from '@/pages/enroll/DataPage';

function isSuccessState(value: unknown): value is SuccessState {
  return (
    value !== null &&
    typeof value === 'object' &&
    'person' in value &&
    typeof (value as SuccessState).person?.fullName === 'string'
  );
}

const POLL_MS = 2_000;
const POLL_MAX = 30;

/**
 * Comprovante. A indexação da face é assíncrona (face-upload → job), então a
 * tela acompanha o `upload_jobs` até ficar `done` — é quando o "Reconhecer"
 * passa a achar a pessoa.
 */
export function SuccessPage() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const data = isSuccessState(state) ? state : null;
  const [index, setIndex] = useState<IndexStatusResponse['status'] | 'waiting'>(
    data?.indexJobId ? 'waiting' : 'unknown',
  );

  useEffect(() => {
    if (!data) navigate('/', { replace: true });
    else clearEnrollment();
  }, [data, navigate]);

  useEffect(() => {
    const jobId = data?.indexJobId;
    if (!jobId) return;
    let polls = 0;
    let timer = 0;
    const tick = async () => {
      polls += 1;
      try {
        const { status } = await api.enroll.indexStatus(jobId);
        setIndex(status);
        if (status === 'done' || status === 'error' || polls >= POLL_MAX) return;
      } catch {
        if (polls >= POLL_MAX) return;
      }
      timer = window.setTimeout(() => void tick(), POLL_MS);
    };
    void tick();
    return () => window.clearTimeout(timer);
  }, [data?.indexJobId]);

  if (!data) return null;

  const indexChip =
    index === 'done'
      ? { label: 'Biometria indexada', color: 'success' as const }
      : index === 'error'
        ? { label: 'Falha na indexação', color: 'error' as const }
        : index === 'unknown'
          ? { label: 'Biometria enviada', color: 'default' as const }
          : { label: 'Indexando biometria…', color: 'warning' as const };

  return (
    <DemoLayout title="Cadastro concluído">
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, pt: 4 }}>
        <CheckCircleIcon color="primary" sx={{ fontSize: 64 }} />
        <Typography variant="h1" textAlign="center">
          Pronto, {data.person.fullName.split(' ')[0]}!
        </Typography>
        <Typography color="text.secondary" textAlign="center">
          Seu rosto já pode ser reconhecido pela câmera.
        </Typography>
      </Box>

      <Box className="result-card result-card--match">
        <span className="result-card__label">Nome</span>
        <span className="result-card__value">{data.person.fullName}</span>
        <span className="result-card__label" style={{ marginTop: 8 }}>
          Documento
        </span>
        <span className="result-card__value">{data.person.documentMasked}</span>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
          <Chip size="small" label="Registro demo" />
          {data.confidence !== null && (
            <Chip size="small" label={`Prova de vida ${Math.round(data.confidence)}%`} />
          )}
          <Chip size="small" color={indexChip.color} label={indexChip.label} />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 'auto', pb: 2 }}>
        <Button variant="contained" fullWidth onClick={() => navigate('/reconhecer')}>
          Testar reconhecimento
        </Button>
        <Button variant="text" fullWidth onClick={() => navigate('/')}>
          Voltar ao início
        </Button>
      </Box>
    </DemoLayout>
  );
}
